import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { Image, Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppSelector } from '../../../hooks/store-hook';
import useTranslation from '../../../hooks/translation-hook';
import {
  areCompetitorCircuitPopupAssetsReady,
  preloadCompetitorCircuitPopupAssets,
} from '../../../helpers/popupAssetPreload';
import CustomButton from '../CustomButton';
import CustomCheckbox from '../CustomCheckbox';
import Label from '../Label';
import Loading from '../Loading';

export const COMPETITOR_CIRCUIT_ACCEPTANCE_CODE = 'CompetitorCircuitAcceptance';

interface CompetitorCircuitAcceptancePopupProps {
  locationCount?: number;
  onConfirm(dontShowAgain: boolean): void;
  onClose(): void;
}

const CompetitorCircuitAcceptancePopup = ({ locationCount, onConfirm, onClose }: CompetitorCircuitAcceptancePopupProps) => {
  const t = useTranslation();
  const layout = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const language = useAppSelector((state) => state.userPrefs.uiLanguage);
  const isFrench = language?.toLowerCase().startsWith('fr');
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [imagesReady, setImagesReady] = useState(areCompetitorCircuitPopupAssetsReady());
  const confirmed = useRef(false);
  const mobile = layout.width < 700;
  const nativeBottomInset = Platform.OS === 'android' ? Math.max(insets.bottom, 0) : 0;
  const popupWidth = mobile ? Math.min(350, Math.max(layout.width - 24, 280)) : 625;
  const popupHeight = mobile
    ? Math.min(680, Math.max(layout.height - 24 - nativeBottomInset, 0))
    : Math.min(600, Math.max(layout.height - 40, 0));
  const mobileHeroHeight = Math.round((popupWidth * 234) / 350);
  // The source asset is 462 x 496, but rendering it at its native size lets
  // the illustration intrude into the longer localized copy on desktop.
  const desktopIllustrationWidth = 370;
  const desktopIllustrationHeight = 397;
  const footerPadding = layout.width > 1200 ? 16 : 12;
  const footerTopPadding = layout.width > 1200 ? 16 : 0;
  const popupBackgroundColor = mobile ? '#FFCCD3' : '#FFD6DB';
  const popupTitleColor = '#4D031A';
  const popupAccentColor = '#8e183d';
  const popupActionColor = '#43A49E';
  const popupBodyColor = '#111111';
  const webNoDragStyle = Platform.OS === 'web' ? ({ userSelect: 'none' } as any) : undefined;

  const imageSource = mobile
    ? require('../../../../assets/competitor-circuit-mobile.png')
    : require('../../../../assets/competitor-circuit-desktop.png');

  useEffect(() => {
    let mounted = true;

    preloadCompetitorCircuitPopupAssets().then(() => {
      if (mounted) {
        setImagesReady(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  const handleConfirm = () => {
    if (confirmed.current) return;

    confirmed.current = true;
    onConfirm(dontShowAgain);
  };

  const renderTextContent = (isMobile: boolean) => (
    <View style={{ maxWidth: isMobile ? undefined : 255, zIndex: 1 }}>
      <Label
        style={{ color: popupTitleColor, fontSize: 28, lineHeight: 34, fontWeight: '700' }}
        numberOfLines={0}
      >
        {t('competitor-circuit-acceptance-title', {
          defaultValue: isFrench ? 'Missions Competitor Circuit' : 'Competitor Circuit Missions',
        })}
      </Label>
      {locationCount !== undefined && (
        <Label style={{ color: popupAccentColor, fontSize: 20, lineHeight: 26, fontWeight: '700', marginTop: 16 }} numberOfLines={0}>
          {t('competitor-circuit-visit-count', {
            count: locationCount,
            defaultValue: isFrench ? '%{count} lieux à visiter' : '%{count} locations to visit',
          })}
        </Label>
      )}
      <Label style={{ color: popupBodyColor, fontSize: 15, lineHeight: 21, marginTop: 18 }} numberOfLines={0}>
        {t('competitor-circuit-acceptance-description', {
          defaultValue: isFrench
            ? 'Visitez les lieux dans l’ordre de votre choix et remplissez un questionnaire à chacun. Une fois tous les questionnaires soumis, un questionnaire comparatif se débloquera.'
            : 'Visit the locations in any order and complete a questionnaire at each one. Once they’re all submitted, a final comparison survey will unlock.',
        })}
      </Label>
      <Label style={{ color: popupBodyColor, fontSize: 15, lineHeight: 21, marginTop: 10 }} numberOfLines={0}>
        {t('competitor-circuit-acceptance-reward', {
          defaultValue: isFrench
            ? 'Votre rémunération totale sera versée lorsque toutes les missions auront été approuvées.'
            : 'Your total reward is released once all missions are approved.',
        })}
      </Label>
      <View style={{ marginTop: 24, alignItems: isMobile ? 'stretch' : 'flex-start' }}>
        <CustomButton
          title={t('competitor-circuit-acceptance-cta', {
            defaultValue: isFrench ? 'Accepter le circuit' : 'Accept the circuit',
          })}
          style={{ backgroundColor: popupActionColor, borderColor: popupActionColor, minWidth: 180 }}
          textStyle={{ color: '#ffffff', fontSize: 14, fontWeight: '600' }}
          onPress={handleConfirm}
        />
      </View>
    </View>
  );

  const renderFooter = () => (
    <View
      style={{
        paddingHorizontal: footerPadding,
        paddingTop: footerTopPadding,
        paddingBottom: footerPadding,
        backgroundColor: popupBackgroundColor,
      }}
    >
      <CustomCheckbox
        value={COMPETITOR_CIRCUIT_ACCEPTANCE_CODE}
        checked={dontShowAgain}
        label={t('Dont-show-this-again', { defaultValue: isFrench ? 'Ne plus afficher' : 'Don’t show again' })}
        onChange={setDontShowAgain}
        textStyle={{ color: popupBodyColor }}
        color={popupAccentColor}
        boxStyle={{ backgroundColor: '#ffffff', borderColor: popupAccentColor }}
      />
    </View>
  );

  const renderBody = () => {
    if (!imagesReady) {
      return (
        <View style={{ minHeight: mobile ? 420 : 520, backgroundColor: popupBackgroundColor, position: 'relative' as any }}>
          <Loading style={{ backgroundColor: 'transparent' }} />
        </View>
      );
    }

    if (mobile) {
      return (
        <View style={[{ width: popupWidth, backgroundColor: popupBackgroundColor }, webNoDragStyle]}>
          <Image
            {...({ pointerEvents: 'none' } as any)}
            source={imageSource}
            accessible={false}
            resizeMode="contain"
            style={{ width: popupWidth, height: mobileHeroHeight, alignSelf: 'center' }}
          />
          <View style={{ paddingHorizontal: 24, paddingTop: 8, paddingBottom: 24 }}>
            {renderTextContent(true)}
          </View>
        </View>
      );
    }

    return (
      <View
        style={{
          minHeight: 520,
          backgroundColor: popupBackgroundColor,
          position: 'relative',
          justifyContent: 'flex-end',
          paddingLeft: 36,
          paddingRight: 36,
          paddingTop: 28,
          paddingBottom: 0,
        }}
      >
        <Image
          {...({ pointerEvents: 'none' } as any)}
          source={imageSource}
          accessible={false}
          resizeMode="contain"
          style={{
            position: 'absolute',
            top: 0,
            right: -1,
            width: desktopIllustrationWidth,
            height: desktopIllustrationHeight,
          }}
        />
        {renderTextContent(false)}
      </View>
    );
  };

  return (
    <View style={[{ width: popupWidth, maxHeight: popupHeight, backgroundColor: popupBackgroundColor, position: 'relative' }, webNoDragStyle]}>
      <ScrollView
        style={{ flexGrow: 0, width: popupWidth, maxHeight: popupHeight, backgroundColor: popupBackgroundColor }}
        contentContainerStyle={{ backgroundColor: popupBackgroundColor }}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        bounces={false}
        alwaysBounceVertical={false}
        alwaysBounceHorizontal={false}
        overScrollMode="never"
      >
        {renderBody()}
        {renderFooter()}
      </ScrollView>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('close', { defaultValue: isFrench ? 'Fermer' : 'Close' })}
        onPress={onClose}
        hitSlop={8}
        style={{
          position: 'absolute',
          right: 12,
          top: 12,
          zIndex: 1,
          width: 34,
          height: 34,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 17,
          backgroundColor: popupBackgroundColor,
        }}
      >
        <FontAwesomeIcon icon={faXmark} size={24} color="#000000" />
      </Pressable>
    </View>
  );
};

export default CompetitorCircuitAcceptancePopup;
